import { useCurrentLocale } from '@shared/hooks/intl';
import useMountEffect from '@shared/hooks/useMountEffect';
import { createScriptLoader } from '@shared/util/dom';
import { getConfigString, TEST } from '@shared/util/env';
import { useState } from 'react';
import useOauthLogin from '../useOauthLogin';

const loader = createScriptLoader('gapi-sdk');

const inject = () =>
  TEST ? Promise.resolve(true) : loader('https://accounts.google.com/gsi/client');

const initialHandler = () =>
  Promise.reject<Awaited<ReturnType<ReturnType<typeof useOauthLogin>>>>(
    new Error('GSI not loaded'),
  );

const Google = () => {
  const clientId = getConfigString('g-client-id');
  const provider = 'google_oauth2';
  const login = useOauthLogin();
  const locale = useCurrentLocale();

  const [handler, setHandler] = useState(() => initialHandler);

  useMountEffect(() => {
    if (!clientId) {
      return;
    }
    if (TEST) {
      setHandler(() => async () => login({ provider, locale }));
      return;
    }
    inject().then(() => {
      let resolve: (value: string) => void;
      let reject: (reason?: unknown) => void;
      const client = google.accounts.oauth2.initCodeClient({
        client_id: clientId,
        scope: 'email profile',
        callback: ({ error, code }) => {
          if (error) {
            reject(error);
          } else {
            resolve(code);
          }
        },
        error_callback: (error) => {
          reject(error);
        },
      });

      setHandler(() => async () => {
        const codePromise = new Promise<string>((_resolve, _reject) => {
          resolve = _resolve;
          reject = _reject;
        });

        client.requestCode();

        const code = await codePromise;

        return login({ provider, code, locale });
      });
    });
  });

  return {
    provider,
    onSignIn: handler,
  };
};

export default Google;
